import { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { avisarVolverAContactar, mensajeDeError, vistaPreviaAviso } from '../../services/volverAContactarService';
import { apellidoNombre } from '../../utils/nombrePersona';

/**
 * Confirmar el aviso de "Volver a contactar" antes de mandarlo.
 *
 * El texto sale de la plantilla (Configuración → WhatsApp) y se puede corregir acá: el operador
 * conoce el caso y a veces conviene nombrar lo que faltaba ("cuando tengas el CUD..."). Nada se
 * manda sin pasar por este modal: es un mensaje real a una persona real.
 */
export default function ModalAvisarVolverAContactar({ show, registro, onCerrar, onAvisado }) {
    const [telefono, setTelefono] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [cargando, setCargando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');

    // La vista previa se pide al abrir: depende de `show` y del id, no del objeto de la fila (que
    // cambia en cada recarga del listado y dispararía un pedido por render).
    const idRegistro = registro?.id;
    useEffect(() => {
        if (!show || !idRegistro) return undefined;
        let vigente = true;
        setCargando(true);
        setError('');
        setMensaje('');
        setTelefono('');
        vistaPreviaAviso(idRegistro)
            .then((data) => {
                if (!vigente) return;
                setTelefono(data?.telefono || '');
                setMensaje(data?.mensaje || '');
            })
            .catch((e) => { if (vigente) setError(mensajeDeError(e, 'No se pudo armar el mensaje.')); })
            .finally(() => { if (vigente) setCargando(false); });
        return () => { vigente = false; };
    }, [show, idRegistro]);

    const enviar = async () => {
        setEnviando(true);
        setError('');
        try {
            const resultado = await avisarVolverAContactar(idRegistro, mensaje);
            onAvisado?.(resultado);
        } catch (e) {
            setError(mensajeDeError(e, 'No se pudo mandar el aviso.'));
        } finally {
            setEnviando(false);
        }
    };

    const sinTelefono = !cargando && !error && !telefono;

    return (
        <Modal show={show} onHide={enviando ? undefined : onCerrar} scrollable centered>
            <Modal.Header closeButton={!enviando}>
                <Modal.Title style={{ fontSize: '1.1rem' }}>
                    <i className="bi bi-whatsapp me-2" style={{ color: '#16a34a' }} />Avisar por WhatsApp
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {registro && (
                    <p className="mb-2">
                        <strong>{apellidoNombre(registro.apellido, registro.nombre)}</strong>
                        {telefono && <span className="text-muted"> · {telefono}</span>}
                    </p>
                )}
                {registro?.fechaUltimoAviso && (
                    <div className="alert alert-info py-2 small">
                        Ya se le avisó el {new Date(registro.fechaUltimoAviso).toLocaleDateString('es-AR')}
                        {registro.usuarioAviso ? ` (${registro.usuarioAviso})` : ''}.
                    </div>
                )}
                {cargando ? (
                    <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : sinTelefono ? (
                    <div className="alert alert-warning py-2 small mb-0">
                        La persona no tiene un teléfono válido en el turno. Cargalo en el turno para poder avisarle.
                    </div>
                ) : !error && (
                    <>
                        <label className="form-label small fw-semibold" htmlFor="vac-mensaje">Mensaje</label>
                        <textarea
                            id="vac-mensaje"
                            className="form-control"
                            rows={7}
                            maxLength={1000}
                            value={mensaje}
                            onChange={(e) => setMensaje(e.target.value)}
                        />
                        <div className="form-text">
                            Sale por el número de WhatsApp del estudio, el de los avisos. Si la persona responde, el mensaje llega a Mensajería.
                        </div>
                    </>
                )}
                {error && <div className="alert alert-danger py-2 small mt-3 mb-0">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onCerrar} disabled={enviando}>Cancelar</Button>
                <Button
                    variant="success"
                    onClick={enviar}
                    disabled={enviando || cargando || sinTelefono || !mensaje.trim()}
                >
                    {enviando ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-send me-2" />}
                    Enviar WhatsApp
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
